import React from 'react';
import { useNavigate } from 'react-router-dom';
import { applyTypography, BaseButton, useT } from '@repo/common';
import { ReactComponent as LocationIcon16 } from '@repo/common/assets/icons/16px/icon_16px_location.svg';
import { ReactComponent as CheckIcon16 } from '@repo/common/assets/icons/16px/icon_16px_check.svg';
import { ReactComponent as ClockIcon16 } from '@repo/common/assets/icons/16px/icon_16px_clock.svg';
import {
  formatWorkshopPrice,
  toNumericPrice,
} from '../utils/priceUtils';

const BagItem = ({
  item = {
    id: 1,
    name: 'Workshop name',
    image: '',
    location:
      '온유여월, 28-10 Jangan-dong, Paldal-gu, Suwon-si, Gyeonggi-do, South Korea',
    date: 'Fri ∙ 5 Dec 2025',
    time: '18:00 - 20:00PM',
    price: 10000,
    originalPrice: null,
    isFree: false,
    isSelected: true,
    isDisabled: false,
    isExpired: false,
  },
  onToggle,
}) => {
  const t = useT();
  const navigate = useNavigate();
  const isUnavailable = item.isDisabled || item.isExpired;

  const handleCardClick = () => {
    if (item.workshopId && !isUnavailable) {
      navigate(`/community/kpathport/workshop/${item.workshopId}`);
    }
  };

  // 통화별 표기 — KRW(계좌이체 워크숍) "55,000won", USD 는 상세 페이지와 같은 "15.00$".
  // 이전엔 통화를 보지 않고 전부 "won" 을 붙여 15달러 워크숍이 "15won" 으로 보였다.
  const currency = item.currency || 'USD';
  const formattedPrice = item.isFree
    ? 'Free'
    : toNumericPrice(item.price)
      ? formatWorkshopPrice(item.price, currency)
      : null;
  const formattedOriginalPrice = toNumericPrice(item.originalPrice)
    ? formatWorkshopPrice(item.originalPrice, currency)
    : null;

  return (
    <div className='relative flex items-start w-full shrink-0'>
      {/* Checkbox — expired 아이템은 체크박스 활성화 (remove 가능하도록) */}
      <div className='flex items-start pr-[8px] tabletH:pr-[16px] pt-[8px] tabletH:pt-[12px] relative self-stretch shrink-0'>
        <BaseButton
          type='text'
          size='s'
          htmlType='button'
          isStatic
          onClick={(e) => {
            e.stopPropagation();
            onToggle?.(item.id);
          }}
          role='checkbox'
          aria-checked={item.isSelected}
          icon={
            item.isSelected ? (
              <div className='relative shrink-0 size-[16px]'>
                <CheckIcon16 className='w-full h-full text-grayscale-gray00' />
              </div>
            ) : null
          }
          className={`content-stretch min-w-0 p-[2px] relative rounded-[4px] shrink-0 size-[20px] transition-colors ${
            item.isSelected
              ? 'bg-functional-success03 border-none cursor-pointer'
              : 'bg-grayscale-gray00 border border-grayscale-gray03 cursor-pointer'
          }`}
        />
      </div>

      {/* Item Card */}
      <div
        onClick={handleCardClick}
        className={`border border-solid content-stretch flex flex-[1_0_0] gap-[8px] tabletH:gap-[16px] items-start min-h-px min-w-px pl-[8px] tabletH:pl-[12px] pr-[12px] tabletH:pr-[16px] py-[8px] tabletH:py-[12px] relative rounded-[15px] ${
          isUnavailable
            ? 'border-grayscale-gray02 bg-grayscale-gray01 cursor-default'
            : 'border-grayscale-gray04 cursor-pointer'
        }`}
      >
        {/* Image */}
        <div className='relative rounded-[8px] shrink-0 size-[90px] tabletH:size-[120px]'>
          <div
            aria-hidden='true'
            className='absolute inset-0 pointer-events-none rounded-[8px]'
          >
            <div className='absolute bg-grayscale-gray01 inset-0 rounded-[8px]' />
            {item.image && (
              <img
                alt=''
                className='absolute max-w-none object-cover rounded-[8px] size-full'
                src={item.image}
              />
            )}
            {isUnavailable && (
              <div className='absolute bg-surface-lightgray80 inset-0 rounded-[8px]' />
            )}
          </div>
        </div>

        {/* Content */}
        <div className='content-stretch flex flex-[1_0_0] flex-col items-start justify-center min-h-px min-w-px relative'>
          {/* Title and Info */}
          <div className='content-stretch flex flex-col items-start pb-[6px] tabletH:pb-[8px] relative shrink-0 w-full'>
            <div className='content-stretch flex items-center gap-[8px] pb-[6px] tabletH:pb-[8px] relative shrink-0 w-full min-w-0'>
              <p
                className={`font-["Mona_Sans:Medium",sans-serif] text-[16px] tabletH:text-[18px] leading-[1.5] ${
                  isUnavailable ? 'text-text-disable' : 'text-text-primary'
                } min-w-0 break-words`}
              >
                {item.name}
              </p>
              {item.isBankTransfer && (
                <span
                  className={`${applyTypography(
                    'b11Strong',
                  )} shrink-0 rounded-[4px] border border-grayscale-gray03 bg-grayscale-gray01 px-[6px] py-[2px] text-text-secondary`}
                  title={t(
                    'Paid by bank transfer, not by card',
                    '카드가 아닌 계좌이체로 결제합니다',
                  )}
                >
                  {t('Bank transfer', '계좌이체')}
                </span>
              )}
              {item.isPendingPayment && !isUnavailable && (
                <span
                  className={`${applyTypography(
                    'b11Strong',
                  )} shrink-0 rounded-[4px] bg-purple-purple01 px-[6px] py-[2px] text-text-accent`}
                  title={t(
                    'You already have a booking waiting for payment. Pay now continues that booking.',
                    '이미 결제 대기 중인 예약이 있습니다. Pay now 를 누르면 그 예약으로 이어집니다.',
                  )}
                >
                  {t('Payment pending', '결제 대기')}
                </span>
              )}
            </div>
            <div className='relative flex flex-col items-start w-full content-stretch shrink-0'>
              {/* Location */}
              <div className='content-stretch flex items-center justify-between pb-[4px] tabletH:pb-[6px] relative shrink-0 w-full'>
                <div className='content-stretch flex flex-[1_0_0] gap-[4px] tabletH:gap-[8px] items-start tabletH:items-center min-h-px min-w-px relative'>
                  <LocationIcon16
                    className={`overflow-clip relative shrink-0 size-[16px] ${
                      isUnavailable ? 'text-text-disable' : 'text-text-primary'
                    }`}
                  />
                  <p
                    className={`flex-[1_0_0] font-["Mona_Sans:Regular",sans-serif] text-[12px] tabletH:text-[13px] leading-[1.4] min-h-px min-w-px ${
                      isUnavailable ? 'text-text-disable' : 'text-text-primary'
                    } shrink-0 whitespace-pre-wrap`}
                  >
                    {item.location}
                  </p>
                </div>
              </div>
              {/* Time */}
              <div className='content-stretch flex gap-[4px] tabletH:gap-[8px] items-start tabletH:items-center relative shrink-0 w-full min-w-0'>
                <ClockIcon16
                  className={`overflow-clip relative shrink-0 size-[16px] ${
                    isUnavailable ? 'text-text-disable' : 'text-text-primary'
                  }`}
                />
                <p
                  className={`font-["Mona_Sans:Regular",sans-serif] text-[12px] tabletH:text-[13px] leading-[1.4] ${
                    isUnavailable ? 'text-text-disable' : 'text-text-primary'
                  } min-w-0 break-words`}
                >
                  {item.date} ∙ {item.time}
                </p>
              </div>
            </div>
          </div>

          {/* Price */}
          <div className='content-stretch flex gap-[8px] items-center justify-end relative shrink-0 w-full'>
            {item.isFree && formattedOriginalPrice ? (
              <>
                <p
                  className={`${applyTypography(
                    'b13',
                  )} text-text-placeholder shrink-0 line-through`}
                  style={{ lineHeight: '1.4' }}
                >
                  {t(
                    `${formattedOriginalPrice}`,
                    `${formattedOriginalPrice?.replace('won', '원')}`,
                  )}
                </p>
                <p
                  className={`${applyTypography(
                    'b16Strong',
                  )} text-text-accent shrink-0`}
                  style={{ lineHeight: '1.5' }}
                >
                  {t('Free', '무료')}
                </p>
              </>
            ) : formattedPrice ? (
              <p
                className={`${applyTypography('b16Strong')} ${
                  isUnavailable ? 'text-text-disable' : 'text-text-primary'
                } shrink-0 text-right`}
                style={{ lineHeight: '1.5' }}
              >
                {formattedPrice === 'Free'
                  ? t('Free', '무료')
                  : t(
                      `${formattedPrice}`,
                      `${formattedPrice?.replace('won', '원')}`,
                    )}
              </p>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BagItem;
